import { Query, TileSettings, Transformation } from '../types/tileBuilderTypes';
import { TileDefinition } from '../types/dashboard';
import { getOp, isMultiSourceOp, TRANSFORMATION_OPS } from './transformations/registry';

// Chart and transformation config
export const chartOptions = [
  { value: 'horizontal_bar_chart', label: 'Horizontal Bar Chart' },
  { value: 'vertical_bar_chart', label: 'Vertical Bar Chart' },
  { value: 'donut_chart', label: 'Donut Chart' },
  { value: 'pie_chart', label: 'Pie Chart' },
  { value: 'gauge_chart', label: 'Gauge Chart' },
  { value: 'line_chart', label: 'Line Chart' },
  { value: 'datagrid', label: 'Data Table' },
  { value: 'metricchart', label: 'Metric Chart' },
];

// Derived from the declarative op registry so there is a single source of truth. The
// raw-JS `script` type is handled by the pipeline engine directly (Advanced escape hatch)
// and is intentionally not part of the registry.
export const TRANSFORMATION_TYPES = TRANSFORMATION_OPS.map((op) => ({ value: op.type, label: op.label }));

// ---- Mapping/Variable Substitution Utilities ----

// Safely evaluates mapping expressions with 'result' in scope.
export function evaluateResultMapping(
  resultMapping: Record<string, string>,
  previousResult: any
): Record<string, string> {
  const evaluated: Record<string, string> = {};
  for (const [k, v] of Object.entries(resultMapping || {})) {
    if (typeof v === "string" && v.includes("result")) {
      try {
        // eslint-disable-next-line no-new-func
        const res = new Function("result", `return (${v});`)(previousResult);
        if (Array.isArray(res)) evaluated[k] = res.join(",");
        else evaluated[k] = String(res);
      } catch (e) {
        console.warn("Failed to eval mapping", k, v, e);
        evaluated[k] = v;
      }
    } else {
      evaluated[k] = v;
    }
  }
  return evaluated;
}

export function substituteVariables(str: string, variables: Record<string, string>) {
  return str.replace(/\{(\w+)}/g, (_, k) => variables[k] ?? `{${k}}`);
}

// ---- Chart and Data Helpers (unchanged) ----

export const getTransformationDisplayName = (transformation: Transformation): string => {
  const { type, logic } = transformation.transformation;
  if (type === 'script') {
    return `Advanced Script: ${logic ? logic.split('\n')[0].substring(0, 30) + '...' : 'Empty'}`;
  }
  const op = getOp(type);
  return op ? op.label : `Unknown (${type})`;
};

// ----------- Predefined Transformation Helper -----------

// Dispatches to the declarative op registry (src/support/transformations/registry.ts).
// Multi-source ops receive `data` as an array-of-arrays; all others receive a flat row
// array. Unknown types degrade gracefully to a flattened pass-through.
const applyPredefinedTransformation = (type: string, config: any, data: any): any => {
  const ensureFlatArray = (input: any): any[] => {
    if (Array.isArray(input)) {
      if (input.length > 0 && Array.isArray(input[0])) {
        return input.flat();
      }
      return input;
    }
    return [];
  };

  const op = getOp(type);
  if (!op) {
    console.warn(`Unknown predefined transformation type: ${type}`);
    return ensureFlatArray(data);
  }

  // Multi-source ops want the raw array-of-arrays; single-source ops want flat rows.
  const input = op.multiSource ? (Array.isArray(data) ? data : []) : ensureFlatArray(data);
  return op.apply(config || {}, input);
};

// ----------- Pipeline Step Outputs (DEFENSIVE) -----------

export const getPipelineStepOutputs = (
  queries: Query[],
  transformations: Transformation[],
  upTo?: number,
  availableVariables: Record<string, string> = {}
): any[] => {
  const queryResults = queries.map(q => q.queryResult ?? []);
  const outputs: any[] = [];
  const limit = typeof upTo === 'number' ? upTo : transformations.length;

  // warn-once registry (global across calls in this runtime)
  const globalAny = globalThis as any;
  if (!globalAny.__tileWarned) {
    globalAny.__tileWarned = new Set<string>();
  }
  const warned: Set<string> = globalAny.__tileWarned;

  const warnOnce = (key: string, message: string) => {
    if (warned.has(key)) return;
    warned.add(key);
    console.warn(message);
  };

  for (let i = 0; i < limit; ++i) {
    const transformation = transformations[i];
    let inputSourcesForThisStep: any[][] = [];

    if (transformation.queryIndices && transformation.queryIndices.length > 0) {
      inputSourcesForThisStep = transformation.queryIndices.map(refIdx => {
        if (refIdx < queries.length) return queryResults[refIdx] || [];
        const outputIdx = refIdx - queries.length;
        if (outputIdx < outputs.length) return outputs[outputIdx];
        return [];
      });
    } else {
      if (i === 0) {
        inputSourcesForThisStep = queryResults.filter(Boolean);
      } else if (outputs.length > 0) {
        inputSourcesForThisStep = [outputs[outputs.length - 1]].filter(Boolean);
      } else {
        inputSourcesForThisStep = [];
      }
    }

    let transformedResult: any;
    const expectsMultipleSources = isMultiSourceOp(transformation.transformation.type);
    const finalInputData = expectsMultipleSources ? inputSourcesForThisStep : inputSourcesForThisStep.flat();

    if (transformation.transformation.type === 'script') {
      try {
        const fn = new Function('data', transformation.transformation.logic || '');
        let out = fn(finalInputData);

        if (Array.isArray(out) && out.length > 0 && typeof out[0] !== 'object') {
          out = out.map(v => ({ value: v }));
          warnOnce(
            'custom_script_primitives_array',
            "Custom script returned array of primitives. Automatically wrapping as array of objects with key 'value'."
          );
        }

        if (!Array.isArray(out)) {
          out = [{ value: out }];
          warnOnce(
            'custom_script_non_array',
            "Custom script did not return an array. Automatically wrapping as [{ value: ... }]."
          );
        }

        transformedResult = out;
      } catch (e: any) {
        console.error(`Error executing custom script transformation for step ${i + 1}:`, e.message);
        transformedResult = null;
        throw e;
      }
    } else {
      transformedResult = applyPredefinedTransformation(
        transformation.transformation.type,
        transformation.transformation.config,
        finalInputData
      );
    }

    if (Array.isArray(transformedResult)) {
      transformedResult = transformedResult.filter(row => typeof row === 'object' && row !== null);
    }

    outputs.push(transformedResult);
  }

  if (transformations.length === 0) {
    let mergedResults = queryResults.flat();
    if (!Array.isArray(mergedResults)) mergedResults = [mergedResults];
    mergedResults = mergedResults.filter(row => typeof row === 'object' && row !== null);
    return [mergedResults];
  }

  return outputs;
};

// === Chart Data/Config Structuring Helpers ===

export function getAggregatedBarChartDataAndConfig(
  data: any[],
  labelField: string,
  valueField: string | null,
  aggregationType: 'Count' | 'Sum' | null = null,
  xAxisLabel?: string,
  yAxisLabel?: string,
) {
  // Defensive: only allow array of objects
  if (!Array.isArray(data) || !labelField) return { keys: [], data: [], config: { series: [] } };
  data = data.filter(row => typeof row === "object" && row !== null);

  let aggregatedData: any[] = [];
  let keys: string[] = [];
  let series: any[] = [];

  if (aggregationType === 'Count') {
    const countMap = data.reduce((acc, row) => {
      const label = row[labelField];
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    keys = Object.keys(countMap);

    aggregatedData = keys.map((key) => ({
      label: key,
      values: keys.map((k) => ({
        key: k,
        value: k === key ? countMap[key] : 0,
      })),
    }));

    series = keys.map((key) => ({
      dataKey: key,
      name: key,
      color: '#8884d8',
    }));
  } else if (aggregationType === 'Sum' && valueField) {
    const sumMap = data.reduce((acc, row) => {
      const label = row[labelField];
      const value = parseFloat(row[valueField]) || 0;
      acc[label] = (acc[label] || 0) + value;
      return acc;
    }, {} as Record<string, number>);

    keys = Object.keys(sumMap);

    aggregatedData = keys.map((key) => ({
      label: key,
      values: keys.map((k) => ({
        key: k,
        value: k === key ? sumMap[key] : 0,
      })),
    }));

    series = keys.map((key) => ({
      dataKey: key,
      name: key,
      color: '#82ca9d',
    }));
  } else {
    keys = [valueField || 'value'];
    aggregatedData = data.map((row) => ({
      label: row[labelField],
      values: keys.map((key) => ({
        key,
        value: row[key],
      })),
    }));

    series = keys.map((key) => ({
      dataKey: key,
      name: key,
      color: '#8884d8',
    }));
  }

  return {
    keys,
    data: aggregatedData,
    config: {
      series,
      xAxis: { label: xAxisLabel || labelField },
      yAxis: { label: yAxisLabel || valueField || 'Value' },
    },
  };
}

export function getDonutPieGaugeDataAndConfig(
  data: any[],
  labelField: string,
  valueField: string | null,
  aggregationType: 'Count' | 'Sum' | null = null,
): { data: { id: string; label: string; value: number }[]; config: any } {
  if (!Array.isArray(data) || !labelField) return { data: [], config: {} };
  data = data.filter(row => typeof row === "object" && row !== null);

  let aggregatedData: any[] = [];

  if (aggregationType === 'Count') {
    const countMap = data.reduce((acc, row) => {
      const label = row[labelField];
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    aggregatedData = Object.entries(countMap).map(([label, count]) => ({
      id: label,
      label,
      value: count,
    }));
  } else if (aggregationType === 'Sum' && valueField) {
    const sumMap = data.reduce((acc, row) => {
      const label = row[labelField];
      const value = parseFloat(row[valueField]) || 0;
      acc[label] = (acc[label] || 0) + value;
      return acc;
    }, {} as Record<string, number>);

    aggregatedData = Object.entries(sumMap).map(([label, sum]) => ({
      id: label,
      label,
      value: sum,
    }));
  } else {
    aggregatedData = data.map((row) => ({
      id: row[labelField],
      label: row[labelField],
      value: valueField ? row[valueField] || 0 : 0,
    }));
  }

  return {
    data: aggregatedData,
    config: {
      chartType: 'donut_chart',
      title: 'Preview',
      settings: {
        margin: {
          top: 20,
          right: 30,
          left: 20,
          bottom: 20,
        },
      },
      series: [],
      extras: {},
    },
  };
}

export function getLineChartDataAndConfig(
  data: any[],
  labelField: string,
  valueField: string | null,
  aggregationType: 'Count' | 'Sum' | null = null,
  xAxisLabel?: string,
  yAxisLabel?: string,
): any {
  if (!Array.isArray(data) || !labelField) return [];
  data = data.filter(row => typeof row === "object" && row !== null);

  let aggregatedData: any[] = [];

  if (aggregationType === 'Count') {
    const countMap = data.reduce((acc, row) => {
      const label = row[labelField];
      if (label !== null && label !== undefined) {
        acc[label] = (acc[label] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    aggregatedData = Object.entries(countMap).map(([label, count]) => ({
      x: label,
      y: count,
    }));

  } else if (aggregationType === 'Sum' && valueField) {
    const sumMap = data.reduce((acc, row) => {
      const label = row[labelField];
      const value = parseFloat(row[valueField]) || 0;
      if (label !== null && label !== undefined) {
        acc[label] = (acc[label] || 0) + value;
      }
      return acc;
    }, {} as Record<string, number>);

    aggregatedData = Object.entries(sumMap).map(([label, sum]) => ({
      x: label,
      y: sum,
    }));

  } else {
    aggregatedData = data.map((row) => ({
      x: row[labelField] ?? null,
      y: valueField ? row[valueField] ?? 0 : 0,
    })).filter((point) => point.x !== null && point.y !== null);
  }

  const lineChartData = aggregatedData.filter((point) => point.x !== null && point.y !== null);
  return { 'id': labelField, data: lineChartData };
}

export function getMetricChartDataAndConfig(
  data: any[],
  labelField: string,
  valueField: string | null,
  aggregationType: 'Count' | 'Sum' | null = null,
): { data: { label: string; value: number }[]; config: any } {
  if (!Array.isArray(data) || !labelField) return { data: [], config: {} };
  data = data.filter(row => typeof row === "object" && row !== null);

  let aggregatedData: any[] = [];

  if (aggregationType === 'Count') {
    const countMap = data.reduce((acc, row) => {
      const label = row[labelField];
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    aggregatedData = Object.entries(countMap).map(([label, count]) => ({
      [labelField]: label,
      value: count,
    }));
  } else if (aggregationType === 'Sum' && valueField) {
    const sumMap = data.reduce((acc, row) => {
      const label = row[labelField];
      const value = parseFloat(row[valueField]) || 0;
      acc[label] = (acc[label] || 0) + value;
      return acc;
    }, {} as Record<string, number>);

    aggregatedData = Object.entries(sumMap).map(([label, sum]) => ({
      [labelField]: label,
      value: sum,
    }));
  } else {
    aggregatedData = data.map((row) => ({
      [labelField]: row[labelField],
      value: valueField ? row[valueField] || 0 : 0,
    }));
  }

  return {
    data: aggregatedData.map((row) => ({
      label: row[labelField],
      value: row.value,
    })),
    config: {
      unit: 'Value',
      settings: {},
    },
  };
}

export function getPreviewTileDataAndDefinition(
  chartId: string,
  tileDefinition: TileDefinition,
  transformedData: any[],
  fields: string[],
  settings: TileSettings
): { previewTileData: any; tileDef: TileDefinition } {
  const {
    selectedColumns,
    labelField,
    valueField,
    chartMargin,
    legendEnabled,
    legendDefault,
  } = settings;

  let previewTileData: any;

  const looksLikeLineSeries =
    Array.isArray(transformedData) &&
    transformedData.length > 0 &&
    typeof transformedData[0] === "object" &&
    transformedData[0] !== null &&
    "id" in transformedData[0] &&
    Array.isArray((transformedData[0] as any).data) &&
    (
      ((transformedData[0] as any).data.length === 0) ||
      (
        typeof (transformedData[0] as any).data[0] === "object" &&
        (transformedData[0] as any).data[0] !== null &&
        "x" in (transformedData[0] as any).data[0] &&
        "y" in (transformedData[0] as any).data[0]
      )
    );

  if (!fields.length || !transformedData.length) {
    previewTileData =
      tileDefinition.chart_type === "datagrid"
        ? { columns: [], rows: [] }
        : tileDefinition.chart_type === "metric_chart"
          ? { data: [] }
          : [];
  } else {
    const isAggregationField = ["Count", "Sum"].includes(valueField);

    switch (tileDefinition.chart_type) {
      case "vertical_bar_chart":
      case "horizontal_bar_chart": {
        // If the incoming data is already "pivoted" (wide) for stacked bars:
        // [{ label: email, "Tech Close": n, "Validate": n, ... }]
        const first = Array.isArray(transformedData) ? transformedData[0] : null;
        const inferred = first
          ? Object.keys(first).filter(k => k !== "label" && k !== "__tooltip" && typeof (first as any)[k] === "number")
          : [];
      
        if (inferred.length > 1) {
          // Pass through directly for Nivo bar: indexBy=label, keys=inferred
          previewTileData = {
            data: transformedData,
            keys: inferred,
            config: {
              series: inferred.map(k => ({ dataKey: k, name: k })),
            },
          };
          break;
        }
      
        // Otherwise, keep existing behavior (raw rows -> aggregated helper)
        const isAggregationField = ["Count", "Sum"].includes(valueField);
        previewTileData = getAggregatedBarChartDataAndConfig(
          transformedData,
          labelField || fields[0],
          isAggregationField ? null : valueField || fields[1],
          isAggregationField ? (valueField as "Count" | "Sum") : null
        );
        break;
      }

      case "donut_chart":
      case "gauge_chart":
      case "pie_chart":
        previewTileData = getDonutPieGaugeDataAndConfig(
          transformedData,
          labelField || fields[0],
          isAggregationField ? null : valueField || fields[1],
          isAggregationField ? (valueField as "Count" | "Sum") : null
        );
        break;

      case "line_chart": {
        // If transformations already produced Harbor/Nivo line series, pass-through
        if (looksLikeLineSeries) {
          previewTileData = { data: transformedData, config: {} };
          break;
        }

        // Otherwise, fall back to your existing row->series helper.
        // IMPORTANT: wrap it as an array because HbrLineChart expects Serie[]
        const single = getLineChartDataAndConfig(
          transformedData,
          labelField || fields[0],
          isAggregationField ? null : valueField || fields[1],
          isAggregationField ? (valueField as "Count" | "Sum") : null
        );

        previewTileData = { data: Array.isArray(single) ? single : [single], config: {} };
        break;
      }

      case "datagrid": {
        const flattenedRows = transformedData.map((row) => {
          const flattenedRow = {};
          const flattenObject = (obj, parentKey = "") => {
            for (const key in obj) {
              if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const newKey = parentKey ? `${parentKey}.${key}` : key;
                if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
                  flattenObject(obj[key], newKey);
                } else {
                  flattenedRow[newKey] = obj[key];
                }
              }
            }
          };
          flattenObject(row);
          return flattenedRow;
        });

        const effectiveColumns =
          Array.isArray(selectedColumns) && selectedColumns.length > 0
            ? selectedColumns
            : Object.keys(flattenedRows[0] || {});

        const filteredRows = flattenedRows.map((row) =>
          effectiveColumns.reduce((acc, column) => {
            if (column in row) acc[column] = (row as any)[column];
            return acc;
          }, {} as any)
        );

        const columns = effectiveColumns.map((col) => ({
          prop: col,
          name: col,
        }));

        previewTileData = {
          columns: columns,
          rows: filteredRows,
          config: {
            type: "datagrid",
            chartType: "datagrid",
            settings: {
              margin: {
                top: 20,
                right: 30,
                left: 20,
                bottom: 20,
              },
            },
            series: [],
            extras: {},
          }
        };
        break;
      }

      case "metricchart":
        previewTileData = getMetricChartDataAndConfig(
          transformedData,
          labelField || fields[0],
          isAggregationField ? null : valueField || fields[1],
          isAggregationField ? (valueField as "Count" | "Sum") : null
        );
        break;

      default:
        previewTileData = [];
        break;
    }
  }

  const tileTypeMap: Record<string, string> = {
    bar_vertical: "vertical_bar_chart",
    bar_horizontal: "horizontal_bar_chart",
    line: "line_chart",
    donut: "donut_chart",
    pie: "pie_chart",
    gauge: "gauge_chart",
    datatable: "datagrid",
    metricchart: "metricchart",
  };

  const tileDef: TileDefinition = {
    ...tileDefinition,
    id: Number(chartId.split('-')[1]),
    settings: {
      ...settings,
      chartMargin,
      show_legend: legendEnabled,
      legendDefault: legendDefault,
    },
    columns: previewTileData?.columns || [],
  };

  if (tileDefinition.chart_type === "line_chart") {
      const td: any = tileDef;
      td.config = td.config || {}; // create config container even though it's not typed

      td.config.chartType = "line_chart";
      td.config.enableSlices = settings?.enableSlices ?? "x";
      td.config.xScale = settings?.xScale ?? { type: settings?.xScaleType ?? "point" };
      td.config.yScale = settings?.yScale ?? {
        type: settings?.yScaleType ?? "linear",
        min: settings?.yScaleMin ?? "auto",
        max: settings?.yScaleMax ?? "auto",
        stacked: settings?.yScaleStacked ?? false,
        reverse: settings?.yScaleReverse ?? false,
      };
    }

  return { previewTileData, tileDef };
}

export function flattenFields  (data) {
  const flattenObjectKeys = (obj, parentKey = "") => {
    let keys = [];
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const newKey = parentKey ? `${parentKey}.${key}` : key;
        if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
          keys = [...keys, ...flattenObjectKeys(obj[key], newKey)];
        } else {
          keys.push(newKey);
        }
      }
    }
    return keys;
  };

  return data.length > 0 ? flattenObjectKeys(data[0]) : [];
}
